'use client'

import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'

import { parseConfigField } from '@/features/dashboard/hooks/use-page-tabs-sync'
import { localeNumberFormat, type NumberFormatPrefs, SEPARATOR_STYLES } from '@/lib/number-format'
import { getComponentPreference, saveComponentPreference } from '@/service/ui-state.api'
import { defaultOrderAccountByTradeVenueAtom } from '@/state/order'
import {
  CHART_ENGINES,
  type ChartEngine,
  chartEngineAtom,
  hideBalancesAtom,
  layoutLockedAtom,
  numberFormatAtom,
} from '@/state/storage'

const GENERAL_SETTINGS_COMPONENT_ID = 'general-settings'
const SAVE_DEBOUNCE_MS = 800

export interface GeneralSettings {
  chartEngine: ChartEngine
  numberFormat: NumberFormatPrefs
  hideBalances: boolean
  layoutLocked: boolean
  defaultOrderAccounts: Record<number, string>
}

// Same once-per-user-session flags as usePageTabsSync (see there for why they're module scope
// and why saves only arm once the hydration fetch settles).
let hydratedUserKey: string | null = null
let saveArmedUserKey: string | null = null
let lastSyncedSnapshot: string | null = null

/** Called from the logout reset path, so a same-user re-login re-hydrates from the server. */
export function resetGeneralSettingsSyncState() {
  hydratedUserKey = null
  saveArmedUserKey = null
  lastSyncedSnapshot = null
}

function isStringRecord(value: unknown): value is Record<number, string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((v) => typeof v === 'string')
  )
}

/**
 * The server copy's fields, each validated on its own. A missing or malformed field is left
 * out, so the local value stands for it (e.g. a record saved before a field existed).
 */
export function parseGeneralSettings(
  config: Record<string, string> | undefined
): Partial<GeneralSettings> {
  const parsed: Partial<GeneralSettings> = {}

  const chartEngine = parseConfigField<unknown>(config, 'chartEngine', null)
  if (CHART_ENGINES.includes(chartEngine as ChartEngine)) {
    parsed.chartEngine = chartEngine as ChartEngine
  }

  const numberFormat = parseConfigField<Partial<NumberFormatPrefs> | null>(
    config,
    'numberFormat',
    null
  )
  if (numberFormat?.separators && numberFormat.separators in SEPARATOR_STYLES) {
    parsed.numberFormat = { separators: numberFormat.separators }
  }

  const hideBalances = parseConfigField<unknown>(config, 'hideBalances', null)
  if (typeof hideBalances === 'boolean') parsed.hideBalances = hideBalances

  const layoutLocked = parseConfigField<unknown>(config, 'layoutLocked', null)
  if (typeof layoutLocked === 'boolean') parsed.layoutLocked = layoutLocked

  const defaultOrderAccounts = parseConfigField<unknown>(config, 'defaultOrderAccounts', null)
  if (isStringRecord(defaultOrderAccounts)) parsed.defaultOrderAccounts = defaultOrderAccounts

  return parsed
}

export function serializeGeneralSettings(settings: GeneralSettings): Record<string, string> {
  return {
    chartEngine: JSON.stringify(settings.chartEngine),
    numberFormat: JSON.stringify(settings.numberFormat),
    hideBalances: JSON.stringify(settings.hideBalances),
    layoutLocked: JSON.stringify(settings.layoutLocked),
    defaultOrderAccounts: JSON.stringify(settings.defaultOrderAccounts),
  }
}

/**
 * Two-way sync between the General settings atoms (localStorage) and the backend's
 * component-preference store, so chart engine, number format, hide-balances, layout lock and
 * the per-venue default order accounts follow the user across devices. The server copy wins
 * on load, field by field; a user with no server record yet uploads their local settings.
 * Local changes are pushed up debounced. The trade-sound toggle stays device-level.
 */
export function useGeneralSettingsSync({
  isLoggedIn,
  userId,
}: {
  isLoggedIn: boolean
  userId?: string | null
}) {
  const [chartEngine, setChartEngine] = useAtom(chartEngineAtom)
  const [numberFormat, setNumberFormat] = useAtom(numberFormatAtom)
  const [hideBalances, setHideBalances] = useAtom(hideBalancesAtom)
  const [layoutLocked, setLayoutLocked] = useAtom(layoutLockedAtom)
  const [defaultOrderAccounts, setDefaultOrderAccounts] = useAtom(
    defaultOrderAccountByTradeVenueAtom
  )

  const settings: GeneralSettings = {
    chartEngine,
    numberFormat,
    hideBalances,
    layoutLocked,
    defaultOrderAccounts,
  }
  const settingsRef = useRef(settings)
  settingsRef.current = settings
  // Re-runs the save effect once hydration settles, so a first-time user's local settings
  // upload without waiting for their next change.
  const [armedFor, setArmedFor] = useState<string | null>(null)

  useEffect(() => {
    if (!isLoggedIn || !userId || hydratedUserKey === userId) return

    // A different user hydrating in this JS session must not inherit (or upload) the
    // previous user's in-memory settings.
    if (hydratedUserKey && hydratedUserKey !== userId) {
      setChartEngine('tradingview')
      setNumberFormat(localeNumberFormat())
      setHideBalances(false)
      setLayoutLocked(false)
      setDefaultOrderAccounts({})
    }
    hydratedUserKey = userId
    saveArmedUserKey = null
    lastSyncedSnapshot = null
    const localAtFetch = JSON.stringify(settingsRef.current)

    getComponentPreference(GENERAL_SETTINGS_COMPONENT_ID)
      .then((pref) => {
        const server = parseGeneralSettings(pref?.config)
        // Nothing on the server yet: keep the local settings; the save effect uploads them.
        if (!Object.keys(server).length) return
        // Changed locally while the fetch was in flight: that fresher copy wins and is uploaded.
        if (JSON.stringify(settingsRef.current) !== localAtFetch) return

        if (server.chartEngine) setChartEngine(server.chartEngine)
        if (server.numberFormat) setNumberFormat(server.numberFormat)
        if (server.hideBalances !== undefined) setHideBalances(server.hideBalances)
        if (server.layoutLocked !== undefined) setLayoutLocked(server.layoutLocked)
        if (server.defaultOrderAccounts) setDefaultOrderAccounts(server.defaultOrderAccounts)

        lastSyncedSnapshot = JSON.stringify({ ...settingsRef.current, ...server })
      })
      .catch((err) => {
        hydratedUserKey = null // retry hydration on the next mount
        console.warn('[generalSettingsSync] failed to load preferences', err)
      })
      .finally(() => {
        saveArmedUserKey = userId
        setArmedFor(userId)
      })
  }, [
    isLoggedIn,
    userId,
    setChartEngine,
    setNumberFormat,
    setHideBalances,
    setLayoutLocked,
    setDefaultOrderAccounts,
  ])

  const serialized = JSON.stringify(settings)
  useEffect(() => {
    if (!isLoggedIn || !userId || saveArmedUserKey !== userId || armedFor !== userId) return
    if (serialized === lastSyncedSnapshot) return

    const timer = setTimeout(() => {
      lastSyncedSnapshot = serialized
      saveComponentPreference({
        componentId: GENERAL_SETTINGS_COMPONENT_ID,
        columnOrder: [],
        config: serializeGeneralSettings(JSON.parse(serialized) as GeneralSettings),
      }).catch((err) => {
        lastSyncedSnapshot = null // let the next change retry the upload
        console.warn('[generalSettingsSync] failed to save preferences', err)
      })
    }, SAVE_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [isLoggedIn, userId, serialized, armedFor])
}
